import { stockTransferInputSchema, type Page, type StockTransfer } from "@cafegestor/shared";
import { useQuery } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useCan } from "@/app/me";
import { Alert, Button, Card, Field, Input, PageTitle, Spinner, Table } from "@/components/ui";
import { apiFetch } from "@/lib/api";
import { Pager, SaveError } from "../cadastros/widgets";
import { DocItemsEditor, docItemsBody, emptyDocItem, type DocItemLine } from "./DocItems";
import { dateTimeBR, qty, useDocAction } from "./lib";
import { DocStatusBadge, ReverseAction, WarehouseSelect } from "./pickers";
import { MovementsTable } from "./SaldosPage";

/**
 * Transferência entre depósitos que não são de vendedor (fábrica, terceiros...). O estoque
 * do vendedor só se movimenta pelo menu Cargas (carregamento, devolução e acerto).
 */
export function TransferenciasPage() {
  const can = useCan();
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const list = useQuery({
    queryKey: ["estoque", "transferencias", offset],
    queryFn: () =>
      apiFetch<Page<StockTransfer>>(`/estoque/transferencias?limit=50&offset=${offset}`),
    placeholderData: (p) => p,
  });

  return (
    <div className="space-y-6">
      <PageTitle
        title="Transferências entre depósitos"
        description="Sai da origem pelo lote que vence primeiro (nunca vencido) e entra igual no destino, com o mesmo custo. Estoque de vendedor não entra aqui: ele se movimenta só por carga, devolução e acerto. Correção é por estorno."
        actions={
          can("estoque.transferencia.lancar") && (
            <Button onClick={() => setCreating(true)} disabled={creating}>
              Nova transferência
            </Button>
          )
        }
      />
      {creating && (
        <TransferForm onClose={() => setCreating(false)} onSaved={(id) => setViewing(id)} />
      )}
      {viewing && <TransferView id={viewing} onClose={() => setViewing(null)} />}
      <Card>
        {list.isPending ? (
          <Spinner />
        ) : (
          <>
            <Table head={["Número", "Quando", "Origem", "Destino", "Motivo", "Situação", " "]}>
              {list.data?.items.map((t) => (
                <tr key={t.id}>
                  <td className="px-3 py-2 font-mono">{t.number}</td>
                  <td className="px-3 py-2">{dateTimeBR(t.confirmedAt ?? t.createdAt)}</td>
                  <td className="px-3 py-2">{t.originWarehouseName}</td>
                  <td className="px-3 py-2">{t.destinationWarehouseName}</td>
                  <td className="px-3 py-2">{t.reason}</td>
                  <td className="px-3 py-2">
                    <DocStatusBadge status={t.status} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => setViewing(t.id)}>
                      Abrir
                    </Button>
                  </td>
                </tr>
              ))}
            </Table>
            <Pager total={list.data?.total ?? 0} offset={offset} onOffset={setOffset} />
          </>
        )}
      </Card>
    </div>
  );
}

function TransferForm({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: (id: string) => void;
}) {
  const [originWarehouseId, setOrigin] = useState("");
  const [destinationWarehouseId, setDestination] = useState("");
  const [reason, setReason] = useState("");
  const [lines, setLines] = useState<DocItemLine[]>([emptyDocItem(1)]);
  const [formError, setFormError] = useState<string | null>(null);
  const save = useDocAction<unknown, StockTransfer>({
    path: () => "/estoque/transferencias",
    invalidate: [["estoque"]],
    onDone: (r) => {
      onClose();
      onSaved(r.id);
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    const parsed = stockTransferInputSchema.safeParse({
      originWarehouseId,
      destinationWarehouseId,
      reason,
      items: docItemsBody(lines),
    });
    if (!parsed.success) {
      const i = parsed.error.issues[0];
      return setFormError(
        `${i?.path.length ? `Campo ${i.path.join(".")}: ` : ""}${i?.message ?? "Dados inválidos"}`,
      );
    }
    setFormError(null);
    save.mutate(parsed.data);
  }

  return (
    <Card title="Nova transferência">
      <form onSubmit={submit} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Origem">
            <WarehouseSelect value={originWarehouseId} onChange={setOrigin} excludeVendor />
          </Field>
          <Field label="Destino">
            <WarehouseSelect
              value={destinationWarehouseId}
              onChange={setDestination}
              excludeVendor
            />
          </Field>
          <Field label="Motivo" hint="Obrigatório: aparece na auditoria e no livro">
            <Input value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
        </div>
        <DocItemsEditor lines={lines} onChange={setLines} />
        <div className="flex gap-2">
          <Button type="submit" disabled={save.isPending}>
            Confirmar transferência
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
        </div>
        {formError && <Alert tone="error">{formError}</Alert>}
        <SaveError error={save.error} />
      </form>
    </Card>
  );
}

function TransferView({ id, onClose }: { id: string; onClose: () => void }) {
  const can = useCan();
  const doc = useQuery({
    queryKey: ["estoque", "transferencias", "detalhe", id],
    queryFn: () => apiFetch<StockTransfer>(`/estoque/transferencias/${id}`),
  });
  const reverse = useDocAction<{ reason: string }, StockTransfer>({
    path: () => `/estoque/transferencias/${id}/estornar`,
    invalidate: [["estoque"]],
  });
  const t = doc.data;
  return (
    <Card
      title={t ? `Transferência ${t.number}` : "Transferência"}
      actions={
        <Button size="sm" variant="secondary" onClick={onClose}>
          Fechar
        </Button>
      }
    >
      {!t ? (
        <Spinner />
      ) : (
        <div className="space-y-4 text-sm">
          <div className="flex flex-wrap gap-6">
            <DocStatusBadge status={t.status} />
            <span>
              {t.originWarehouseName} → {t.destinationWarehouseName}
            </span>
            <span>Motivo: {t.reason}</span>
            <span>Por: {t.createdByEmail ?? "—"}</span>
            <span>Em: {dateTimeBR(t.confirmedAt)}</span>
          </div>
          {t.status === "estornado" && (
            <Alert tone="warn">
              Estornada em {dateTimeBR(t.reversedAt)}. Motivo: {t.reversalReason}
            </Alert>
          )}
          <Table head={["Produto", "Quantidade"]}>
            {t.items?.map((i) => (
              <tr key={i.id}>
                <td className="px-3 py-2">
                  <span className="font-mono text-xs">{i.productCode}</span> {i.productName}
                </td>
                <td className="px-3 py-2 text-right">{qty(i.quantity, i.unitCode)}</td>
              </tr>
            ))}
          </Table>
          <div>
            <h3 className="mb-2 font-semibold">Movimentos (lotes)</h3>
            <MovementsTable rows={t.movements ?? []} />
          </div>
          {t.status === "confirmado" && can("estoque.transferencia.lancar") && (
            <ReverseAction
              pending={reverse.isPending}
              error={reverse.error}
              onConfirm={(reason) => reverse.mutate({ reason })}
            />
          )}
        </div>
      )}
    </Card>
  );
}
